import React from 'react';
import { Paper, Typography, Stack, Divider, Box, Switch, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { useUpdateCustomerStatus } from '../../../hooks/customers/useUpdateCustomerStatus';

const Field = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight="medium">{value || '-'}</Typography>
    </Box>
);

const CustomerProfileTab = ({ customer }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { updateCustomerStatusMutate, isUpdatingStatus } = useUpdateCustomerStatus();

    if (!customer) return null;

    const isActive = customer.status === 'active';

    const toggle = () => {
        updateCustomerStatusMutate({
            id: customer.id,
            data: { status: isActive ? 'banned' : 'active' },
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
            }}
        >
            <Typography variant="h6" fontWeight={700} mb={2}>
                {isArabic ? 'بيانات الحساب' : 'Account Details'}
            </Typography>

            <Stack
                divider={<Divider flexItem />}
                spacing={2}
            >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" sx={{ rowGap: 2 }}>
                    <Field label={isArabic ? 'الاسم' : 'Name'} value={customer.name} />
                    <Field label={isArabic ? 'البريد' : 'Email'} value={customer.email} />
                    <Field label={isArabic ? 'الهاتف' : 'Phone'} value={customer.phone} />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" sx={{ rowGap: 2 }}>
                    <Field
                        label={isArabic ? 'تأكيد البريد' : 'Email Verified'}
                        value={
                            customer.email_verified_at
                                ? dayjs(customer.email_verified_at).format('YYYY-MM-DD HH:mm')
                                : (isArabic ? 'غير مؤكد' : 'Not verified')
                        }
                    />
                    <Field
                        label={isArabic ? 'تأكيد الهاتف' : 'Phone Verified'}
                        value={
                            customer.phone_verified_at
                                ? dayjs(customer.phone_verified_at).format('YYYY-MM-DD HH:mm')
                                : (isArabic ? 'غير مؤكد' : 'Not verified')
                        }
                    />
                    <Field
                        label={isArabic ? 'اللغة' : 'Language'}
                        value={customer.lang === 'ar' ? 'العربية' : (customer.lang === 'en' ? 'English' : customer.lang)}
                    />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap" sx={{ rowGap: 2 }}>
                    <Field
                        label={isArabic ? 'تاريخ التسجيل' : 'Registered At'}
                        value={customer.created_at ? dayjs(customer.created_at).format('YYYY-MM-DD HH:mm') : '-'}
                    />
                    <Field
                        label={isArabic ? 'آخر دخول' : 'Last Login'}
                        value={customer.last_login_at ? dayjs(customer.last_login_at).format('YYYY-MM-DD HH:mm') : '-'}
                    />
                    <Field
                        label={isArabic ? 'الأدوار' : 'Roles'}
                        value={
                            Array.isArray(customer.roles) && customer.roles.length
                                ? customer.roles.join(', ')
                                : '-'
                        }
                    />
                </Stack>

                <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>
                        {isArabic ? 'حالة الحساب' : 'Account Status'}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="body2" fontWeight="medium" color={isActive ? 'success.main' : 'error.main'}>
                            {isActive
                                ? (isArabic ? 'نشط — يمكنه تسجيل الدخول' : 'Active — login allowed')
                                : (isArabic ? 'محظور — لا يمكنه تسجيل الدخول' : 'Banned — login blocked')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={toggle}
                                    disabled={isUpdatingStatus}
                                    color="primary"
                                />
                            }
                            label=""
                            sx={{ m: 0 }}
                        />
                    </Box>
                </Box>
            </Stack>
        </Paper>
    );
};

export default CustomerProfileTab;
